import React, { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

// The one project/scope model: a client from the list, the SITE NAME only, and a fixed scope letter.
// The bid name is composed (Client - Project - Scope) and the number minted on the server.
export const SCOPES = [
  { letter: "M", key: "mechanical", label: "Mechanical" },
  { letter: "D", key: "electrical_dc", label: "Electrical DC" },
  { letter: "A", key: "electrical_ac", label: "Electrical AC" },
  { letter: "S", key: "substation", label: "Substation" },
  { letter: "C", key: "civil", label: "Civil" },
  { letter: "V", key: "commissioning", label: "Commissioning" },
  { letter: "E", key: "full_epc", label: "EPC" },
  { letter: "T", key: "tm_labor", label: "T&M Labor" },
];

export const scopeLabel = (keyOrLetter) => (SCOPES.find((s) => s.key === keyOrLetter || s.letter === keyOrLetter) || {}).label || keyOrLetter;

const PROJECT_WARN = /\b\d+(?:\.\d+)?\s*(?:mw|mwdc|mwac|mwp)\b|\b(mechanical|mech|electrical|elec|civil|substation|commissioning|epc|t&m|dc|ac|mv)\b/i;

export function ScopeTag({ letter, active = true, testid }) {
  return (
    <span data-testid={testid} title={scopeLabel(letter)}
      className={`inline-flex items-center justify-center w-[18px] h-[18px] rounded text-[10px] font-bold ${active ? "bg-[#003E87] text-white" : "bg-slate-100 text-slate-400"}`}>
      {letter}
    </span>
  );
}

export function ScopePicker({ value, onChange, multiple = false, taken = [], testid = "scope-picker" }) {
  const selected = multiple ? (value || []) : (value ? [value] : []);
  const toggle = (k) => {
    if (multiple) onChange(selected.includes(k) ? selected.filter((x) => x !== k) : [...selected, k]);
    else onChange(k === value ? null : k);
  };
  return (
    <div className="flex flex-wrap gap-1.5" data-testid={testid}>
      {SCOPES.map((s) => {
        const on = selected.includes(s.key);
        const used = taken.includes(s.key) && !on;
        return (
          <button key={s.key} type="button" disabled={used} onClick={() => toggle(s.key)} data-testid={`${testid}-${s.letter}`}
            title={used ? `${s.label} is already a bid on this project` : s.label}
            className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-xs transition ${on ? "border-[#003E87] bg-[#003E87] text-white" : used ? "border-slate-100 text-slate-300" : "border-slate-200 text-slate-700 hover:border-[#003E87]"}`}>
            <span className="font-bold">{s.letter}</span> {s.label}
          </button>
        );
      })}
    </div>
  );
}

export function useClients() {
  const [clients, setClients] = useState([]);
  useEffect(() => { api.get("/clients").then((r) => setClients(r.data || [])).catch(() => {}); }, []);
  return clients;
}

export const matchClient = (clients, text) => {
  const t = (text || "").trim().toLowerCase();
  if (!t) return null;
  return clients.find((c) => [c.name, c.display_name, c.legal_name, ...(c.aliases || [])].some((n) => (n || "").toLowerCase() === t))
    || clients.find((c) => (c.name || "").toLowerCase().startsWith(t)) || null;
};

// Client (from the list) + Project (site name) + Scope(s) with the composed names shown read-only.
export function ProjectIdentityFields({ value, onChange, multiple = false, idPrefix = "ident", clients: given }) {
  const own = useClients();
  const clients = given || own;
  const set = (k, v) => onChange({ ...value, [k]: v });
  const client = clients.find((c) => c.client_id === value.client_id);
  const disp = client?.display_name || client?.name || "";
  const warn = PROJECT_WARN.test(value.project || "");
  const scopes = multiple ? (value.scopes || []) : (value.scope ? [value.scope] : []);
  const names = useMemo(() => scopes.map((k) => [disp || "Client", (value.project || "Project").trim(), scopeLabel(k)].join(" - ")), [scopes, disp, value.project]);
  return (
    <div className="space-y-3">
      <div>
        <Label className="text-xs">Client <span className="text-rose-500">*</span></Label>
        <Select value={value.client_id || ""} onValueChange={(v) => set("client_id", v)}>
          <SelectTrigger data-testid={`${idPrefix}-client`}><SelectValue placeholder="Pick the client from the list" /></SelectTrigger>
          <SelectContent>
            {clients.map((c) => (
              <SelectItem key={c.client_id} value={c.client_id}>{c.display_name || c.name}{c.aliases?.length ? ` (${c.aliases.join(", ")})` : ""}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      <div>
        <Label className="text-xs">Project — site name only <span className="text-rose-500">*</span></Label>
        <Input data-testid={`${idPrefix}-project`} value={value.project || ""} onChange={(e) => set("project", e.target.value)} placeholder="Orbit Road" />
        {warn && <div className="text-[11px] text-amber-700 mt-1" data-testid={`${idPrefix}-project-warn`}>Keep MW, state and scope words out of the site name — they have their own fields.</div>}
      </div>
      <div>
        <Label className="text-xs">Scope{multiple ? "s" : ""} <span className="text-rose-500">*</span></Label>
        <div className="mt-1.5">
          <ScopePicker multiple={multiple} value={multiple ? value.scopes : value.scope} onChange={(v) => set(multiple ? "scopes" : "scope", v)} testid={`${idPrefix}-scope`} />
        </div>
        <div className="text-[11px] text-slate-400 mt-1">Plain “Electrical” is not a scope — pick DC or AC.</div>
      </div>
      {names.length > 0 && (
        <div className="rounded-md bg-slate-50 border border-slate-200 px-3 py-2" data-testid={`${idPrefix}-preview`}>
          <div className="text-[10px] uppercase tracking-wide text-slate-400">Bid name{names.length > 1 ? "s" : ""} (composed)</div>
          {names.map((n) => <div key={n} className="text-sm text-slate-800 font-medium">{n}</div>)}
        </div>
      )}
    </div>
  );
}
